<template>
    <Head :title="__('Create :resource', {resource: __('The Price').toLowerCase()})"/>

    <BreezeDashboardLayout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                {{ __('Create :resource', {resource: __('The Price').toLowerCase()}) }}
            </h2>
        </template>

        <notification v-if="Object.keys(errors).length" color="danger">
            <ul class="list-inside">
                <li v-for="error in errors">{{ error }}</li>
            </ul>
        </notification>

        <the-modal-box v-model="isConfirmChangeSiteModalActive" :title="__('Please confirm action')" has-cancel
                       @confirm="confirmChangeSite" @cancel="cancelChangeSite">
            <p>{{ __('Are you sure you want to change the site? It will remove selected nomenclature.') }}</p>
        </the-modal-box>

        <card-component :title="__('Price Settings')" :icon="mdiListStatus" @submit.prevent="submit" form>
            <div class="md:grid md:grid-cols-2 md:gap-2">
                <div>
                    <field :label="__('Site')" :error="errors.site ?? ''">
                        <control :icon="mdiLink" :placeholder="__('Site')" v-model="formSite" :options="siteOptions"
                                 :required="true"/>
                    </field>
                    <field :label="__('Client login')" :error="errors.name ?? ''">
                        <control :icon="mdiAccount" :placeholder="__('Client login')" v-model="form.name"
                                 :required="true" :autocompleteOptions="nameOptions" autocompleteLabel="username"
                                 :isLoading="isLoading" @autocompleteOptionSelect="nameAutocompleteOptionSelect"
                                 @keyup="asyncFind"/>
                    </field>
                    <field :label="__('Client Site')" :error="errors.client_site ?? ''">
                        <control :icon="mdiOpenInNew" :placeholder="__('Client Site')" v-model="form.client_site"/>
                    </field>
                    <field :label="__('Email')" :error="errors.email ?? ''">
                        <control :icon="mdiEmail" :placeholder="__('Email')" v-model="form.email"
                                 :required="true"/>
                    </field>
                    <field class="max-w-sm" :label="__('Format')" :error="errors.file_format ?? ''">
                        <control :icon="mdiPuzzleOutline" :options="fileFormatOptions" v-model="form.file_format"
                                 :required="true"/>
                    </field>
                </div>
                <div>
                    <field class="mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="usd_currency"
                            type="switch"
                            v-model="form.usd_currency"
                            :options="{ usd_currency: __('USD Currency') }"
                        ></check-radio-picker>
                    </field>
                    <field>
                        <check-radio-picker
                            class="-ml-7"
                            name="only_available_products"
                            type="switch"
                            v-model="form.only_available_products"
                            :options="{ only_available_products: __('Only availability in stock') }"
                            :readonly="availableProductsReadonly"
                        />
                    </field>
                    <field>
                        <check-radio-picker
                            class="-ml-7"
                            name="sample-switch"
                            type="switch"
                            v-model="form.exchange_changes"
                            :options="{ exchange_changes: __('Update when the exchange rate changes') }"
                        />
                    </field>
                    <catalog-selector v-model="form.nomenclatures" :site="formSite" class="mt-11"/>
                    <stores-selector v-model="form.stores" :site="formSite"/>
                </div>
            </div>
            <field class="max-w-sm" :label="__('Update interval')"
                   :help="__('0-8 times a day, starting at 6 o\'clock in the morning')"
                   :error="errors.update_interval ?? ''">
                <control :icon="mdiAvTimer" :options="updateIntervalOptions" v-model="form.update_interval"
                         :required="true"/>
            </field>
            <field class="max-w-sm" :label="__('Price language')" :error="errors.price_language ?? ''">
                <control :icon="mdiTranslate" :options="languageOptions" v-model="form.price_language"
                         :required="true"/>
            </field>
        </card-component>

        <div class="md:grid md:grid-cols-3 md:gap-2 mt-4">
            <card-component :title="__('Price Fields Settings')" :icon="mdiViewColumnOutline">
                <div class="md:grid md:grid-cols-2 md:gap-2 mt-4">
                    <field :error="errors.price_fields ?? ''" class="col-span-1">
                        <check-radio-picker class="checkbox-block" type="checkbox mr-0"
                                            name="sample-checkbox"
                                            v-model="form.price_fields"
                                            :options="priceFieldsOptions"
                        />
                    </field>
                    <card-component class="col-span-1">
                        <field :error="errors.rozetka_description ?? ''" class="mb-2 mt-3">
                            <label class="checkbox">
                                <input
                                    type="checkbox"
                                    name="rozetka_description"
                                    v-model="form.rozetka_description"
                                    :disabled="!isPMUser"
                                >
                                <span class="check"></span>
                                <span class="control-label pr-3 pl-0">{{ __('Price rozetka description') }}</span>
                            </label>
                        </field>

                        <field class="max-w-sm mb-2" :label="__('Price additional image')"
                               :error="errors.additional_image ?? ''">
                            <control :icon="mdiImage" :options="additionalImageOptions" v-model="form.additional_image"
                                     :disabled="!isPMUser"
                                     :required="true"/>
                        </field>
                        <field :error="errors.use_special_price ?? ''" class="mb-2 mt-3">
                            <label class="checkbox">
                                <input
                                    type="checkbox"
                                    name="use_special_price"
                                    v-model="form.use_special_price"
                                    :disabled="!isPMUser"
                                >
                                <span class="check"></span>
                                <span class="control-label pr-3 pl-0">{{ __('Use special price') }}</span>
                            </label>
                        </field>
                    </card-component>
                </div>
            </card-component>
            <card-component :title="__('Price Price Settings')" :icon="mdiCurrencyUsd" class="col-span-2">
                <field class="col-span-2" :error="errors.price_prices ?? ''">
                    <prices-picker class="checkbox-grid" type="checkbox mr-0"
                                   name="sample-checkbox"
                                   v-model="form.price_prices"
                                   :options="pricePricesOptions"
                                   :priority="form.price_priority"
                    />
                </field>
            </card-component>
        </div>

        <jb-buttons class="justify-end" mb="">
            <jb-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing"
                       type="button" color="success"
                       :label="__('Create :resource', {resource: __('The Price').toLowerCase()})"
                       @click.prevent="submit"/>
            <jb-button type="button" color="warning" outline :label="__('Cancel')"
                       @click.prevent="$inertia.visit(this.route('price-configs.index'))"/>
        </jb-buttons>

    </BreezeDashboardLayout>
</template>

<style>
</style>

<script>
import BreezeDashboardLayout from '@/Layouts/Dashboard'
import CardComponent from '@/Components/Dashboard/CardComponent'
import TheModalBox from '@/Components/Dashboard/TheModalBox'
import Notification from '@/Components/Dashboard/Notification'
import {Head} from '@inertiajs/inertia-vue3';
import {ref} from 'vue'
import {
    mdiListStatus,
    mdiAccount,
    mdiEmail,
    mdiLink,
    mdiOpenInNew,
    mdiPuzzleOutline,
    mdiAvTimer,
    mdiTranslate,
    mdiNumeric8BoxMultipleOutline,
    mdiCurrencyUsd,
    mdiViewColumnOutline,
    mdiImage,
} from '@mdi/js'
import Field from '@/Components/Dashboard/Field'
import Control from '@/Components/Dashboard/Control'
import CheckRadioPicker from '@/Components/Dashboard/CheckRadioPicker'
import PricesPicker from '@/Components/Dashboard/PricesPicker'
import JbButton from '@/Components/Dashboard/JbButton'
import JbButtons from '@/Components/Dashboard/JbButtons'
import CatalogSelector from "@/Components/Dashboard/CatalogSelector"
import StoresSelector from "@/Components/Dashboard/StoresSelector"

export default {
    components: {
        BreezeDashboardLayout,
        Head,
        TheModalBox,
        Notification,
        CardComponent,
        Field,
        Control,
        CheckRadioPicker,
        PricesPicker,
        JbButton,
        JbButtons,
        CatalogSelector,
        StoresSelector
    },
    props: {
        errors: Object,
        priceFieldsOptions: Object,
        pricePricesOptions: Object,
        fileFormatOptions: Array,
        updateIntervalOptions: Array,
        languageOptions: Array,
        siteOptions: Array,
        additionalImageOptions: Array,
        isPMUser: Boolean,
    },
    watch: {
        formSite(newVal, oldVal) {
            if (this.formSiteOldValue === null) {
                if (this.form.nomenclatures.length > 0 || this.form.name !== '' || this.form.email !== '' || this.form.stores.length > 0) {
                    this.isConfirmChangeSiteModalActive = true
                    this.formSiteOldValue = oldVal
                } else {
                    this.form.site = this.formSite
                }
            } else {
                this.formSiteOldValue = null
            }
        },
        "form.stores": {
            handler(newVAl, oldVal) {
                this.availableProductsToggle(newVAl.length > 0)
            },
            deep: true
        }
    },
    data(props) {
        return {
            form: this.$inertia.form({
                name: '',
                site: props.siteOptions[0],
                client_site: '',
                nomenclatures: [],
                stores: [],
                email: '',
                file_format: this.fileFormatOptions[0],
                update_interval: this.updateIntervalOptions[0],
                price_language: this.languageOptions[0],
                usd_currency: false,
                exchange_changes: ['exchange_changes'],
                price_fields: ["1", "2", "3", "4", "5", "7"],
                rozetka_description: false,
                additional_image: this.additionalImageOptions[0],
                use_special_price: false,
                user_price_type_id: 0,
                site_user_id: null,
                price_prices: [],
                only_available_products: false,
                price_priority: 0
            }),
            formSite: props.siteOptions[0],
            formSiteOldValue: null,
            isLoading: false,
            nameOptions: [],
            availableProductsReadonly: false,
        }
    },
    setup() {
        const isConfirmChangeSiteModalActive = ref(false)

        return {
            mdiListStatus,
            mdiAccount,
            mdiLink,
            mdiOpenInNew,
            mdiEmail,
            mdiPuzzleOutline,
            mdiAvTimer,
            mdiTranslate,
            mdiNumeric8BoxMultipleOutline,
            mdiCurrencyUsd,
            mdiViewColumnOutline,
            mdiImage,
            isConfirmChangeSiteModalActive
        }
    },
    beforeMount() {
        document.body.addEventListener('click', this.nameOptionsOutClick);
    },
    methods: {
        availableProductsToggle(setAndBlock = true) {
            if (setAndBlock) {
                this.form.only_available_products = true
                this.availableProductsReadonly = true
            } else {
                this.form.only_available_products = false
                this.availableProductsReadonly = false
            }
        },
        nameOptionsOutClick(e) {
            if (typeof this.nameOptions !== "undefined" && this.nameOptions.length > 0) {
                const el = e.target
                if (el.classList.contains('autocomplete-options') === false ||
                    el.classList.contains('autocomplete-options__option') === false) {
                    this.nameOptions = []
                    this.form.site_user_id = null
                }
            }
        },
        nameAutocompleteOptionSelect(autocompleteOption) {
            this.nameOptions = []
            this.form.email = autocompleteOption.email
            const language_code = this.getLanguageCodeById(autocompleteOption.language_id)
            this.form.price_language = this.languageOptions.find(item => item.id === language_code)
            this.form.price_prices = [autocompleteOption.price_type_id]
            this.form.user_price_type_id = autocompleteOption.price_type_id
            this.form.site_user_id = autocompleteOption.id
            const priceOption = this.pricePricesOptions.find(item => item.api_source_id == autocompleteOption.price_type_id)
            if (priceOption) {
                this.form.price_priority = priceOption.order;
            } else {
                this.form.price_priority = 0;
            }
        },
        getLanguageCodeById(id) {
            let code = 'en'
            if (id == 5) {
                code = 'ru'
            } else if (id == 6) {
                code = 'uk'
            } else if (id == 3) {
                code = 'es'
            }
            return code
        },
        asyncFind(event) {
            const self = this

            if (event.target.value.length < 3) {
                this.nameOptions = []
                return
            }

            const query = event.target.value

            if (this.isLoading === true) {
                return
            }

            self.isLoading = true
            axios.post(this.route('api.v1.site-users.index'), {
                'siteId': this.form.site.api_source_id,
                'search': query
            }).then(response => {
                self.isLoading = false
                self.nameOptions = response.data
                let isSameName = false;
                self.nameOptions.forEach((item) => {
                    if (query == item.username && this.form.site_user_id == item.id) {
                        isSameName = true
                    }
                });

                if (!isSameName) {
                    this.form.site_user_id = null
                }
            }).then(error => {
                self.isLoading = false
            });
        },
        submit() {
            this.form.post(this.route('price-configs.store'))
        },
        cancelChangeSite() {
            this.formSite = this.formSiteOldValue;
        },
        confirmChangeSite() {
            this.formSiteOldValue = null
            this.form.site = this.formSite
            this.form.nomenclatures = []
            this.form.stores = []
            this.form.name = ''
            this.form.email = ''
            this.form.price_priority = 0
            this.form.price_prices = []
            this.form.user_price_type_id = 0
            this.form.site_user_id = null
        }
    }
}
</script>
